import React from "react";
import BlogTemplate from "./BlogTemplate";


import DSMHeroImage from "../../assets/images/Blogs/UploadDownloadFiles/dsm_hero.png";
import ServerKeyImage from "../../assets/images/Blogs/UploadDownloadFiles/server_key.png";
import OTWInstallerImage from "../../assets/images/Blogs/UploadDownloadFiles/otw_installer.png";
import TEMInstallerImage from "../../assets/images/Blogs/UploadDownloadFiles/tem_installer.png";
import PauseUploadImage from "../../assets/images/Blogs/UploadDownloadFiles/pause_upload.png";
import ResumeUploadImage from "../../assets/images/Blogs/UploadDownloadFiles/resume_upload.png";
import BMIDEConstantsImage from "../../assets/images/Blogs/UploadDownloadFiles/bmide_constants.png";
import UploadErrorImage from "../../assets/images/Blogs/UploadDownloadFiles/upload_error.png";

const UploadDownloadFiles = () => {
  return (
    <BlogTemplate
      seoTitle="Siemens Teamcenter 11.2 Asynchronous File Upload/Download | NexTech PLM"
      seoDescription="Learn how to upload and download large files asynchronously using Data Share Manager (DSM) in Teamcenter 11.2 and apply file upload security policies."
      seoKeywords="Teamcenter 11.2, Data Share Manager, DSM, Asynchronous File Upload, Teamcenter Security Policies, PLM"
      ogImage="https://nextechplm.com/og/upload-download-files.png"
      pageUrl="https://nextechplm.com/blog/upload-download-files"
      bannerTitle="Asynchronous File Upload/Download using Data Share Manager"
    >
      
      {/* Introduction Section */}
      <section className="container content-container">
        <div className="text-container">
          <p>
            This article is focusing on how you can upload or download files using data share manager asynchronously and focusing on how you can apply file upload security policies.
          </p>
          <p>
            It‘s a common business scenario where you need to upload / download large size files such as MS word, CAE result files and so on to/from Teamcenter. Primarily, this calls for a mechanism which can be used for downloading or uploading large size files from/to Teamcenter without keeping your Teamcenter client session running.
          </p>
          <p>
            To address above business requirements, Teamcenter 11.2 now offers Data Share Manager (DSM) to help user to efficiently download/upload the large size files asynchronously and allows end user to view file uploads and downloads and to manage them by pausing, resuming, or canceling the processes.
          </p>
          <p>
            DSM is a separate executable with its own user interface available with Rich Client and Thin Client, supports installation in different modes such stand-alone, using OTW installer and using TEM installer. DSM get automatically initiated during log on or file upload or download action. 
          </p>
        </div>
        <div className="image-container">
           <img src={DSMHeroImage} alt="Data Share Manager Interface in Teamcenter" />
        </div>
      </section>

      {/* Installation & Configuration Section */}
      <section className="blog-section" style={{ backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
        <h2 className="sub-title">How to enable DSM in your Teamcenter Deployment?</h2>

        <h3 className="key-element-title">Server Side Installation & Configuration</h3>
        <p>Export a PUBLIC key for the data share manager.</p>
        <ul className="blog-list">
          <li>Run <code>install_datasharekeys -u=username -p=password -g=dba -f=exp_pubkey</code>.</li>
          <li>This command exports *.pem file and a *.x509 key file, zip *.x509 file in to a keys.zip for end user (individual) user to install DSM.</li>
          <li>End user cannot use the DSM unless it has been installed with a public key that grants access to the Teamcenter database.</li>
          <li>By default, each Teamcenter database contains a private/public key pair that validates Data Share Manager Clients accessing the database.</li>
          <li>Admin can set the <code>Use_DataShare_Manager</code> preference to true (This can be overridden by individual user with "USER" protection scope).</li>
        </ul>
        <div className="image-container" style={{ margin: "2rem auto", width: "100%", maxWidth: "500px" }}>
           <img src={ServerKeyImage} alt="Exported public keys for Data Share Manager" />
        </div>

        <h3 className="key-element-title" style={{ marginTop: "2rem" }}>Client Side Installation & Configuration</h3>
        <p>Distribute the public keys to end users for their usage which can be done in two different ways:</p>
        <ul className="blog-list">
          <li>Distribute the keys.zip file to users which they have to during DSM stand-alone installation.</li>
          <li>Distribute the *.x509 key files to users which they have to place into their keys directory after installation using the stand-alone installer.</li>
        </ul>

        <h4 style={{ marginTop: "1.5rem", fontWeight: "600" }}>Rich Client Installation</h4>
        <p><strong>Over-the-Web (OTW) Installer:</strong> OTW Installer finds the already copied keys.zip file on the local system and uses it to install the Data Share Manager.</p>
        <div className="image-container" style={{ margin: "1rem auto", width: "100%", maxWidth: "700px" }}>
           <img src={OTWInstallerImage} alt="Configuring Data Share Manager via OTW Installer" />
        </div>
        <p><strong>Teamcenter Environment Manager (TEM):</strong> Browse for the KEY.zip files during the TEM installation process.</p>
        <div className="image-container" style={{ margin: "1rem auto", width: "100%", maxWidth: "700px" }}>
           <img src={TEMInstallerImage} alt="Configuring Data Share Manager via TEM" />
        </div>

        <h4 style={{ marginTop: "1.5rem", fontWeight: "600" }}>Thin Client Installation</h4>
        <ul className="blog-list">
          <li>Place the stand-alone installer file in a web application server such as Tomcat or WebLogic.</li>
          <li>Set the <code>WEB_DSM_install_link</code> preference to point to the URL where the stand-alone installer is located, for example, <code>http://yourhost:9090/dsm/install.exe</code>.</li>
        </ul>

        <p className="text-bold">Important Note for Stand-alone Installation:</p>
        <ul className="blog-list">
          <li>Open the Data Share Manager Installation directory.</li>
          <li>RMB on DMLauncher.exe and select Create Shortcut.</li>
          <li>Select the created shortcut and select Cut.</li>
          <li>Paste the shortcut there under the windows Startup folder and rename it to FMS Data Share Manager.</li>
        </ul>
      </section>

      {/* Usage Scenarios Section */}
      <section className="blog-section">
        <h2 className="sub-title">Business Usage Scenarios</h2>
        
        <h3 className="key-element-title">Start upload of large file and close the client session</h3>
        <p>
          Sometime it is not possible to finish the upload of files may be because of large size as long as your client session is ON, in this scenario you want to start upload and close your client session but still want your file upload to happen. Start upload of large file using DSM and close your client it will get uploaded asynchronously irrespective of client session.
        </p>

        <h3 className="key-element-title" style={{ marginTop: "2rem" }}>Pause file upload, shutdown system, and resume later</h3>
        <p>
          Sometime you need to start the file upload but want to leave for the day and hence pause file upload action and close the Teamcenter client and also shut down the machine. Next day when you log on to system, you want to resume file your upload action without starting the Teamcenter client.
        </p>
        <div className="image-container" style={{ margin: "1.5rem auto", width: "100%", maxWidth: "700px" }}>
           <img src={PauseUploadImage} alt="Pausing file upload in DSM" />
        </div>
        <p>
          Once you back and log on to system, resume the file upload using Restore option to start DSM and then Resume option to resume the file upload.
        </p>
        <div className="image-container" style={{ margin: "1.5rem auto", width: "100%", maxWidth: "700px" }}>
           <img src={ResumeUploadImage} alt="Resuming file upload in DSM" />
        </div>
      </section>

      {/* File Upload Security Policies */}
      <section className="blog-section" style={{ backgroundColor: "#f0f8ff", borderRadius: "8px" }}>
        <h2 className="sub-title">File Upload Security Polices</h2>
        <p>
          DSM honor file upload security polices which you can define using BMIDE business constants on dataset to restrict user to upload undesired file to Teamcenter.
        </p>
        
        <h3 className="key-element-title">Configuration</h3>
        <p>Define business Object Constants on dataset as below:</p>
        <div className="image-container" style={{ margin: "1.5rem auto", width: "100%", maxWidth: "700px" }}>
           <img src={BMIDEConstantsImage} alt="BMIDE Business Constants for file upload policies" />
        </div>

        <h3 className="key-element-title">Usage</h3>
        <p>
          User intentionally or un-intentionally upload the file which is not supposed to be uploaded to the system, under this scenario system restrict user if file name/pattern/length/char policies are not satisfied. File upload restrictions are applicable to synchronous as well as asynchronous file upload mode.
        </p>
        <div className="image-container" style={{ margin: "1.5rem auto", width: "100%", maxWidth: "800px" }}>
           <img src={UploadErrorImage} alt="Teamcenter restricting file upload based on security policy" />
        </div>
      </section>

      {/* Business Benefits */}
      <section className="blog-section">
        <h2 className="sub-title">Business Benefits</h2>
        <ul className="blog-list">
          <li>It helps you to upload/download files without keeping your client session open.</li>
          <li>It helps you to restrict file upload based on file upload security policies.</li>
          <li>It helps you to manage file upload such as pause, resume, cancel etc..</li>
          <li>It helps you to resume your file upload even after you shutdown your machine.</li>
          <li>Data Share Manager (DSM) with Active Workspace helps you efficiently download/upload large-sized files asynchronously and eliminate the need of keeping client open during upload/download.</li>
        </ul>
      </section>

    </BlogTemplate>
  );
};

export default UploadDownloadFiles;